import { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';

interface Props {
  value: string;
  /** SVG height in pixels. Width auto-derives from format + value. */
  height?: number;
  /** Pixel width of one bar. Smaller = denser label. Default 1.4. */
  barWidth?: number;
  /** Render the human-readable text underneath. Default true. */
  displayValue?: boolean;
  /** JsBarcode format. EAN-13 is the supermarket default but the lib
   *  also accepts CODE128, UPC, ITF14, etc. Auto-falls back to CODE128
   *  when the supplied value can't satisfy the chosen format. */
  format?: 'CODE128' | 'EAN13' | 'UPC' | 'ITF14';
}

// Thin wrapper around JsBarcode that renders into an inline SVG. Inline
// SVG lives in the DOM directly so the browser's native print can
// scale it crisply at any resolution (vs. canvas which rasterises).

export function Barcode({
  value,
  height = 48,
  barWidth = 1.4,
  displayValue = true,
  format = 'CODE128',
}: Props) {
  const ref = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    try {
      JsBarcode(ref.current, value, {
        format,
        width: barWidth,
        height,
        displayValue,
        fontSize: 12,
        margin: 0,
        // Fixed monospace so the digit row stays aligned across labels.
        font: 'monospace',
        textMargin: 2,
      });
    } catch {
      // JsBarcode throws when the value can't satisfy the format
      // (e.g. EAN-13 needs 12-13 digits). The svg stays empty in that
      // case — the parent shows a fallback string.
    }
  }, [value, height, barWidth, displayValue, format]);

  return <svg ref={ref} aria-label={value} />;
}
